import { FC, createContext, useContext, useReducer, Dispatch, ReactNode } from 'react';
import { BidErrorType } from './constants';
import type { Currency } from './types';

export const STEPS = {
    PAYMENT_VERIFICATION: 'PAYMENT_VERIFICATION',
    SHIPPING_INFO: 'SHIPPING_INFO',
    PLACE_BID: 'PLACE_BID',
    SUCCESS_BID: 'SUCCESS_BID',
} as const;

export const SET_STEP = 'SET_STEP';
export const SET_MAX_BID_AMOUNT = 'SET_MAX_BID_AMOUNT';
export const SET_SHOW_INCREASE_BID = 'SET_SHOW_INCREASE_BID';
export const SET_BID_ERROR_TYPE = 'SET_BID_ERROR_TYPE';
export const SET_MAX_BID_AMOUNT_VALID = 'SET_MAX_BID_AMOUNT_VALID';
export const SET_ATTEMPTED_MAX_BID_AMOUNT = 'SET_ATTEMPTED_MAX_BID_AMOUNT';
export const SET_PLACING_BID = 'SET_PLACING_BID';
export const SET_OVER_BIDDING_WARNING = 'SET_OVER_BIDDING_WARNING';
export const SET_SHOW_LARGE_BID_CONFIRM = 'SET_SHOW_LARGE_BID_CONFIRM';
export const SET_INCREASE_MAX_BID_CLICKED = 'SET_INCREASE_MAX_BID_CLICKED';

export type Step = keyof typeof STEPS;
type Action =
    | { type: typeof SET_STEP; step: Step }
    | { type: typeof SET_MAX_BID_AMOUNT; maxBidAmount: number }
    | { type: typeof SET_SHOW_INCREASE_BID; showIncreaseBid: boolean }
    | { type: typeof SET_BID_ERROR_TYPE; bidErrorType: BidErrorType | null }
    | { type: typeof SET_MAX_BID_AMOUNT_VALID; isMaxBidAmountValid: boolean }
    | { type: typeof SET_ATTEMPTED_MAX_BID_AMOUNT; attemptedMaxBidAmount: number }
    | { type: typeof SET_PLACING_BID; isPlacingBid: boolean }
    | { type: typeof SET_INCREASE_MAX_BID_CLICKED; isIncreaseMaxBidClicked: boolean }
    | {
          type: typeof SET_OVER_BIDDING_WARNING;
          showOverbiddingWarning: boolean;
      }
    | {
          type: typeof SET_SHOW_LARGE_BID_CONFIRM;
          showLargeBidConfirmation: boolean;
          largeBidAmount: number;
      };

type State = {
    step: Step;
    maxBidAmount: number;
    isMaxBidAmountValid: boolean;
    showIncreaseBid: boolean;
    bidErrorType: BidErrorType | null;
    currency: Currency;
    attemptedMaxBidAmount: number;
    isMobile: boolean;
    isBiddingDisabled: boolean;
    isPlacingBid: boolean;
    showOverbiddingWarning: boolean;
    showLargeBidConfirmation: boolean;
    largeBidAmount: number;
    isIncreaseMaxBidClicked: boolean;
};

export type DispatchAction = Dispatch<Action>;

type BiddingModalContextType = {
    state: State;
    dispatch: DispatchAction;
};

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case SET_STEP:
            return { ...state, step: action.step };
        case SET_MAX_BID_AMOUNT:
            return { ...state, maxBidAmount: action.maxBidAmount };
        case SET_BID_ERROR_TYPE:
            return { ...state, bidErrorType: action.bidErrorType };
        case SET_SHOW_INCREASE_BID:
            return { ...state, showIncreaseBid: action.showIncreaseBid };
        case SET_MAX_BID_AMOUNT_VALID:
            return { ...state, isMaxBidAmountValid: action.isMaxBidAmountValid };
        case SET_ATTEMPTED_MAX_BID_AMOUNT:
            return { ...state, attemptedMaxBidAmount: action.attemptedMaxBidAmount };
        case SET_PLACING_BID:
            return { ...state, isPlacingBid: action.isPlacingBid };
        case SET_INCREASE_MAX_BID_CLICKED:
            return { ...state, isIncreaseMaxBidClicked: action.isIncreaseMaxBidClicked };
        case SET_OVER_BIDDING_WARNING:
            return {
                ...state,
                showOverbiddingWarning: action.showOverbiddingWarning,
            };
        case SET_SHOW_LARGE_BID_CONFIRM:
            return {
                ...state,
                showLargeBidConfirmation: action.showLargeBidConfirmation,
                largeBidAmount: action.largeBidAmount,
            };
        default:
            return state;
    }
};

export const AuctionBiddingModalContext = createContext<BiddingModalContextType | null>(null);

const initialState: State = {
    step: STEPS.PLACE_BID,
    maxBidAmount: 0,
    isMaxBidAmountValid: true,
    showIncreaseBid: false,
    bidErrorType: null,
    currency: 'USD',
    attemptedMaxBidAmount: 0,
    isBiddingDisabled: false,
    isPlacingBid: false,
    isMobile: false,
    showOverbiddingWarning: false,
    showLargeBidConfirmation: false,
    largeBidAmount: 0,
    isIncreaseMaxBidClicked: false,
};

export type StateOverrides = Pick<State, 'currency'> & Partial<State> & { children?: ReactNode };

export const useBiddingModalReducer = (
    stateOverrides: StateOverrides
): [State, Dispatch<Action>] => {
    const modalInitialState: State = {
        ...initialState,
        ...stateOverrides,
    };
    return useReducer(reducer, modalInitialState);
};

export const AuctionBiddingModalContextProvider: FC<StateOverrides> = ({
    children,
    ...stateOverrides
}) => {
    const [state, dispatch] = useBiddingModalReducer(stateOverrides);

    return (
        <AuctionBiddingModalContext.Provider value={{ state, dispatch }}>
            {children}
        </AuctionBiddingModalContext.Provider>
    );
};

export const useAuctionBiddingModalContext = (): BiddingModalContextType => {
    const context = useContext(AuctionBiddingModalContext);
    if (!context) {
        throw new Error(
            'useAuctionBiddingModalContext must be used within a AuctionBiddingModalContextProvider'
        );
    }
    return context;
};
